import { useEffect } from 'react';

export type HotkeyHandler = (event: KeyboardEvent) => void;

/**
 * Горячие клавиши приложения (docs/06 §5.3): C, /, B, G, ?, Esc.
 * Не срабатывают, когда фокус в поле ввода, — кроме явно разрешённых.
 */

export type HotkeyMap = Readonly<Record<string, HotkeyHandler>>;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
}

export function useHotkeys(map: HotkeyMap, options: { readonly allowInInputs?: boolean } = {}): void {
  const { allowInInputs = false } = options;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (!allowInInputs && isTypingTarget(event.target)) {
        return;
      }
      const handler = map[event.key.toLowerCase()] ?? map[event.key];
      if (handler) {
        // Печатный символ хоткея не должен попасть в поле, которое получит фокус
        // в результате самого хоткея (например, «c» открывает форму задачи и
        // сразу фокусирует заголовок).
        event.preventDefault();
        handler(event);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [map, allowInInputs]);
}
